
import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { PricingPreview } from './usePricingPreview';

interface UsePricingUpdateProps {
  lineItemId: string;
}

export const usePricingUpdate = ({ lineItemId }: UsePricingUpdateProps) => {
  const [isRefreshing, setIsRefreshing] = useState(false);

  const updatePricing = async (preview: PricingPreview, changeTrigger: string = 'manual_refresh'): Promise<boolean> => {
    try {
      setIsRefreshing(true);

      // Log the pricing change in audit trail
      const { error: auditError } = await supabase.rpc('log_pricing_change', {
        p_line_item_id: lineItemId,
        p_change_trigger: changeTrigger,
        p_previous_base_cost: preview.old.baseCost,
        p_previous_markup_percentage: preview.old.markupPercentage,
        p_previous_markup_source: preview.old.markupSource,
        p_previous_final_price: preview.old.finalPrice,
        p_new_base_cost: preview.new.baseCost,
        p_new_markup_percentage: preview.new.markupPercentage,
        p_new_markup_source: preview.new.markupSource,
        p_new_final_price: preview.new.finalPrice
      });

      if (auditError) throw auditError;

      // Update ONLY base_cost, markup_percentage, markup_source - NEVER final_price
      const { error: updateError } = await supabase
        .from('internal_line_items')
        .update({
          base_cost: preview.new.baseCost,
          markup_percentage: preview.new.markupPercentage,
          markup_source: preview.new.markupSource,
          last_price_calculation: new Date().toISOString()
          // final_price is NEVER stored in DB - always calculated live
        })
        .eq('id', lineItemId);

      if (updateError) throw updateError;

      toast.success('Pricing updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating pricing:', error);
      toast.error('Failed to refresh pricing');
      return false;
    } finally {
      setIsRefreshing(false);
    }
  };

  return {
    isRefreshing,
    updatePricing
  };
};
